import React, { useState, useRef, useEffect, useCallback } from 'react';

function UserEditor({ value, onChange, disabled, onSearchUsers, onAutoSave }) {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
    // Load initial users
    fetchUsers('');
  }, []);

  const fetchUsers = async (query) => {
    if (!onSearchUsers) return;
    setLoading(true);
    try {
      const result = await onSearchUsers(query);
      setUsers(result || []);
    } catch (err) {
      console.error('Failed to search users:', err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearch(query);
    setHighlightedIndex(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchUsers(query), 300);
  };

  const filteredUsers = users.filter(u => u.accountType === 'atlassian' && u.active !== false);

  // Total navigable items: Unassigned (index 0) + filtered users (index 1..N)
  const totalItems = filteredUsers.length + 1;

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('.field-editor-option');
    if (items[highlightedIndex]) {
      items[highlightedIndex].scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const handleSelect = useCallback((user) => {
    if (disabled) return;
    if (onAutoSave) onAutoSave(user);
    else onChange(user);
  }, [disabled, onAutoSave, onChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev + 1) % totalItems);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev <= 0 ? totalItems - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex === 0) {
        handleSelect(null);
      } else if (highlightedIndex > 0 && highlightedIndex <= filteredUsers.length) {
        handleSelect(filteredUsers[highlightedIndex - 1]);
      }
    }
  }, [totalItems, highlightedIndex, filteredUsers, handleSelect]);

  const currentAccountId = value?.accountId;

  return (
    <div className="field-editor-user-wrapper">
      <input
        ref={searchRef}
        type="text"
        className="field-editor-search-input"
        placeholder="Search users..."
        value={search}
        onChange={handleSearchChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      {loading ? (
        <div className="field-editor-loading">Searching...</div>
      ) : (
        <div className="field-editor-options-list" ref={listRef}>
          {/* Unassign option */}
          <div
            className={`field-editor-option ${!currentAccountId ? 'selected' : ''} ${highlightedIndex === 0 ? 'highlighted' : ''}`}
            onClick={() => handleSelect(null)}
            onMouseEnter={() => setHighlightedIndex(0)}
          >
            <span className="field-editor-option-label" style={{ fontStyle: 'italic' }}>
              Unassigned
            </span>
          </div>
          {filteredUsers.map((user, idx) => {
            const isSelected = currentAccountId === user.accountId;
            const itemIndex = idx + 1;
            return (
              <div
                key={user.accountId}
                className={`field-editor-option ${isSelected ? 'selected' : ''} ${highlightedIndex === itemIndex ? 'highlighted' : ''}`}
                onClick={() => handleSelect(user)}
                onMouseEnter={() => setHighlightedIndex(itemIndex)}
              >
                {user.avatarUrls?.['24x24'] && (
                  <img
                    src={user.avatarUrls['24x24']}
                    alt=""
                    className="field-editor-user-avatar"
                  />
                )}
                <span className="field-editor-option-label">{user.displayName}</span>
                {isSelected && <span className="field-editor-option-check">✓</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default UserEditor;
